'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const credentials = [
{ label: 'B.A. Betriebswirtschaft', icon: '🎓' },
{ label: 'NLP-Coach (zertifiziert)', icon: '🧠' },
{ label: 'Breathwork Teacher', icon: '🌬️' },
{ label: '10 Jahre Business & Coaching', icon: '📍' }];


export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('revealed');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal-hidden, .reveal-hidden-left, .reveal-hidden-right');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="ueber-mich" className="py-28 px-6 bg-warm-white">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Photo + floating card */}
          <div className="reveal-hidden-left relative flex justify-center">
            <div className="relative w-full max-w-sm">
              {/* Main photo */}
              <div className="rounded-4xl overflow-hidden shadow-soft-lg aspect-[3/4]">
                <AppImage
                  src="/assets/images/Bildschirmfoto_2026-01-14_um_12.33.23-1771685668749.png"
                  alt="Andrej Martens, Karriere-Coach und Ikiguide, professionelles Portrait eines lächelnden Mannes in natürlichem Licht"
                  fill
                  className="object-cover object-center" />
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 via-transparent to-transparent" />
              </div>

              {/* Floating credential card */}
              <div className="float-gentle glass-card rounded-3xl p-5 absolute -bottom-6 -right-6 shadow-soft-lg max-w-[200px]">
                <p className="text-xs font-bold text-sage-dark uppercase tracking-widest mb-2">
                  Ansatz
                </p>
                <p className="text-sm text-charcoal leading-snug font-medium">
                  Gründungsberatung trifft Conscious Coaching
                </p>
                <div className="mt-3 flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-sage" />
                  <div className="w-2 h-2 rounded-full bg-sand" />
                  <div className="w-2 h-2 rounded-full bg-slate-blue" />
                </div>
              </div>

              {/* Decorative element */}
              <div className="absolute -top-4 -left-4 w-20 h-20 rounded-full border-2 border-sage/20 float-delay-4 pointer-events-none" />
            </div>
          </div>

          {/* Right: Bio */}
          <div className="flex flex-col gap-7 reveal-hidden-right">
            <div>
              <span className="badge-sage rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest inline-block mb-5">
                Ansatz
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal leading-tight">
                Andrej{' '}
                <span className="italic text-sage">Martens</span>
              </h2>
            </div>

            <p className="text-muted text-lg font-light leading-relaxed">
              Ich bin die Brücke zwischen innerer Klarheit und beruflicher Realität.
              Als NLP-Coach, Breathwork Teacher und erfahrener Recruiting-Experte
              verbinde ich das Beste aus zwei Welten.
            </p>

            <p className="text-muted text-base font-light leading-relaxed">
              Nach über <strong className="text-charcoal font-semibold">10 Jahren in Sales, Business Development und Coaching</strong> weiß ich:
              Die meisten Menschen kennen ihre Stärken nicht – oder wissen nicht, wie sie sie auf dem Markt positionieren.
              Genau hier setze ich an. Mit der Ikiguide-Methode verbinde ich spirituelles Wachstum
              mit konkreter Karrierestrategie.
            </p>

            {/* Credentials grid */}
            <div className="grid grid-cols-2 gap-3">
              {credentials?.map((cred, i) =>
              <div
                key={i}
                className={`reveal-hidden delay-${(i + 1) * 100} flex items-center gap-3 bg-cream border border-sand/30 rounded-2xl px-4 py-3`}>
                
                  <span className="text-xl">{cred?.icon}</span>
                  <span className="text-sm font-medium text-charcoal leading-snug">{cred?.label}</span>
                </div>
              )}
            </div>

            {/* Philosophy quote */}
            <blockquote className="border-l-2 border-sage pl-5 reveal-hidden delay-400">
              <p className="font-serif text-xl italic text-charcoal/75 font-light leading-relaxed">
                „Mein Ziel ist nicht, dir zu sagen, was du tun sollst.
                Mein Ziel ist, dir zu helfen, zu hören, was du schon immer gewusst hast."
              </p>
            </blockquote>

            <a
              href="#cta"
              className="btn-primary text-sm font-semibold px-7 py-3.5 rounded-full inline-block text-center w-fit reveal-hidden delay-500">
              
              Mit Andrej sprechen →
            </a>
          </div>
        </div>
      </div>
    </section>);

}